"use client";

import { useEffect, useState } from "react";
import Clarity from "./Clarity";
import GoogleAds from "./GoogleAds";
import { PostHogProvider } from "@/app/providers";

const STORAGE_KEY = "growthrail_analytics_consent";

export default function ConsentAwareAnalytics() {
  const [consent, setConsent] = useState<boolean | null>(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const initialConsent = stored === "granted" ? true : stored === "denied" ? false : null;
    const timer = window.setTimeout(() => setConsent(initialConsent), 0);

    const openPreferences = () => setPreferencesOpen(true);
    window.addEventListener("growthrail:open-consent", openPreferences);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("growthrail:open-consent", openPreferences);
    };
  }, []);

  const choose = (allowed: boolean) => {
    window.localStorage.setItem(STORAGE_KEY, allowed ? "granted" : "denied");
    setConsent(allowed);
    setPreferencesOpen(false);
  };

  const showBanner = consent === null || preferencesOpen;

  return (
    <>
      {consent === true && (
        <>
          <Clarity />
          <GoogleAds />
          <PostHogProvider />
        </>
      )}

      {showBanner && (
        <aside
          className="fixed inset-x-3 bottom-3 z-[120] mx-auto max-w-3xl rounded-2xl border border-white/15 bg-[#14110f]/95 p-5 text-zinc-200 shadow-2xl backdrop-blur-xl sm:inset-x-6 sm:flex sm:items-center sm:gap-6"
          aria-label="Analytics preferences"
        >
          <div className="flex-1">
            <h2 className="font-display text-base font-semibold text-white">Analytics preferences</h2>
            <p className="mt-1 text-sm leading-relaxed text-zinc-400">
              We use optional analytics and advertising measurement to understand the website and improve campaigns. Essential site features work without them.
            </p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:mt-0 sm:flex sm:shrink-0">
            <button
              type="button"
              onClick={() => choose(false)}
              className="min-h-12 rounded-xl border border-white/15 px-4 text-sm font-semibold text-white hover:bg-white/5"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={() => choose(true)}
              className="min-h-12 rounded-xl bg-copper-500 px-4 text-sm font-semibold text-white hover:bg-copper-400"
            >
              Accept analytics
            </button>
          </div>
        </aside>
      )}
    </>
  );
}
